import { call, cancel, fork, put, select, take, delay } from 'redux-saga/effects';

import { IAppState, TActions } from '../../common/state';
import { IRequestMetaResponse, requestMeta } from '../api';
import { ESuburbanOfferFilterActions } from './suburban_offer_filter';
import { saveJsonQueryInLocalStorage } from '../../utils/save_json_query';
import { getSortReplaceCoworkingOfficeType } from '../helpers';
import { IS_BY_COMMERCIAL_OWNER_ACTION_TYPE } from './is_by_commercial_owner';

export interface IMetaRefreshRequestedAction {
  type: 'filters/meta/META_REFRESH_REQUESTED';
}

export interface IMetaRefreshCompleteAction {
  type: 'filters/meta/META_REFRESH_COMPLETE';
  meta: IRequestMetaResponse;
}

export interface IMetaRefreshFailedAction {
  type: 'filters/meta/META_REFRESH_FAILED';
}

export interface IMetaRefreshStoppedAction {
  type: 'filters/meta/META_REFRESH_STOPPED';
}

const actionsThatCouldChangeMeta: TActions['type'][] = [
  'filters/access_system/ACCESS_SYSTEM_CHANGED',
  'filters/advanced/MODAL_CLOSED',
  'filters/advanced/TERM_CHANGED',
  'filters/advanced/TERMS_CHANGED',
  'filters/area/MAX_AREA_CHANGED',
  'filters/area/MIN_AREA_CHANGED',
  'filters/balconies/BALCONIES_CHANGED',
  'filters/balconies/BALCONY_CHANGED',
  'filters/balconies/LOGGIA_CHANGED',
  'filters/bedroomsCount/BEDROOMS_CHANGED',
  'filters/bs_centers/BS_CENTERS_CHANGED',
  'filters/building_class/BUILDING_CLASS_TYPE_CHANGED',
  'filters/building_class/WAREHOUSE_BUILDING_CLASS_TYPE_CHANGED',
  'filters/building_condition/BUILDING_CONDITION_CHANGED',
  'filters/building_crane/BUILDING_CRANE_TYPE_CHANGED',
  'filters/building_elevator/BUILDING_ELEVATOR_TYPE_CHANGED',
  'filters/building_gates/BUILDING_GATES_TYPE_CHANGED',
  'filters/building_heating/BUILDING_HEATING_TYPE_CHANGED',
  'filters/building_type/BUILDING_TYPE_ADDED',
  'filters/building_type/BUILDING_TYPE_BUILDING_ADDED',
  'filters/building_type/BUILDING_TYPE_BUILDING_REMOVED',
  'filters/building_type/BUILDING_TYPE_REMOVED',
  'filters/ceilingHeight/MAX_CEILING_HEIGHT_CHANGED',
  'filters/ceilingHeight/MIN_CEILING_HEIGHT_CHANGED',
  'filters/comission/AGENT_CALLS_FORBIDDEN_CHANGED',
  'filters/comission/COMISSION_TYPE_CHANGED',
  'filters/currencyType/CURRENCY_TYPE_CHANGED',
  'filters/deal_type/DEAL_TYPE_CHANGED',
  'filters/decoration/DECORATION_TYPE_CHANGED',
  'filters/developer/DEVELOPER_TYPE_CHANGED',
  'filters/developer/PROMO_CHANGED',
  'filters/directions_modal/HIGHWAYS_CHANGED',
  'filters/districts/DISTRICT_NEW_MOSCOW_SWITCHED',
  'filters/districts/DISTRICT_REMOVED',
  'filters/districts/DISTRICT_SELECTED',
  'filters/done_year/BUILDING_DONE_CHANGED',
  'filters/done_year/BUILDING_DONE_IN_YEAR_CHANGED',
  'filters/done_year/BUILDING_DONE_LATER_CHANGED',
  'filters/dwelling_status/APARTMENT_CHANGED',
  'filters/dwelling_status/PENTHOUSE_CHANGED',
  'filters/elevators/ELEVATORS_CHANGED',
  'filters/elevators/SERVICE_LIFT_CHANGED',
  'filters/entrance/ENTRANCE_TYPE_CHANGED',
  'filters/facilities/FACILITIES_CHANGED',
  'filters/facilities/FURNITURE_CHANGED',
  'filters/floor/IS_BASEMENT_CHANGED',
  'filters/floor/IS_FIRST_CHANGED',
  'filters/floor/IS_SEMIBASEMENT_CHANGED',
  'filters/floor/MAX_FLOOR_CHANGED',
  'filters/floor/MIN_FLOOR_CHANGED',
  'filters/floor/NOT_LAST_CHANGED',
  'filters/from_mkad/MAX_DISTANCE_CHANGED',
  'filters/from_mkad/MIN_DISTANCE_CHANGED',
  'filters/garage/GARAGE_KIND_CHANGED',
  'filters/garage/GARAGE_MATERIAL_CHANGED',
  'filters/garage/GARAGE_STATUS_CHANGED',
  'filters/garage/GARAGE_TYPE_CHANGED',
  'filters/geo/GEO_SELECTED',
  'filters/heating/HEATING_CHANGED',
  'filters/house_material/SET',
  'filters/house_material/TOGGLED',
  'filters/house_year/MAX_HOUSE_YEAR_CHANGED',
  'filters/house_year/MIN_HOUSE_YEAR_CHANGED',
  'filters/infrastructure/INFRASTRUCTURE_PARAM_CHANGED',
  'filters/is_in_hidden_base/CHANGED',
  'filters/kp/KP_CHANGED',
  'filters/land_status/LAND_STATUS_CHANGED',
  'filters/layout/LAYOUT_TYPE_CHANGED',
  'filters/location_switcher/CITY_SELECTED',
  'filters/location_switcher/CITY_SUGGESTION_SELECTED',
  'filters/location_switcher/LOCATION_SWITCHER_CLOSED',
  'filters/location_switcher/REGION_SELECTED',
  'filters/manufacture_condition/MANUFACTURE_CONDITION_CHANGED',
  'filters/map/MAP_AREA_DELETED_ACTION',
  'filters/map/MAP_CHANGED_ACTION',
  'filters/map/MAP_CLOSED',
  'filters/map/METRO_CLOSED',
  'filters/metro/METRO_REMOVED',
  'filters/metro/METRO_SELECTED',
  'filters/offer_id/OFFER_ID_CHANGED',
  'filters/offer_type/OFFER_TYPE_SELECTED',
  'filters/office_condition/OFFICE_CONDITION_CHANGED',
  'filters/parking/CHANGED',
  'filters/parking/SET',
  'filters/phone/CONTACT_CHANGED',
  'filters/price/MAX_PRICE_CHANGED',
  'filters/price/MIN_PRICE_CHANGED',
  'filters/priceType/JSON_QUERY_PRICE_TYPE_CHANGED',
  'filters/priceType/PRICE_TYPE_CHANGED',
  'filters/property_type/PROPERTY_TYPE_ADDED',
  'filters/property_type/PROPERTY_TYPE_REMOVED',
  'filters/publish_period/PUBLISH_PERIOD_CHANGED',
  'filters/rent_terms/MAX_PREPAY_CHANGED',
  'filters/rent_terms/WITHOUT_DEPOSIT_CHANGED',
  'filters/rent_time/RENT_TIME_CHANGED',
  'filters/repair/REPAIR_TOGGLED',
  'filters/repair/SET',
  'filters/room/ROOM_CHANGED',
  'filters/rooms_in_deal/ROOMS_IN_DEAL_CHANGED',
  'filters/rooms_total/ROOMS_TOTAL_CHANGED',
  'filters/sale_type/MORTGAGE_CHANGED',
  'filters/sale_type/SALE_TYPE_CHANGED',
  'filters/sale_type/SALE_TYPE_SET',
  'filters/sort/SORT_CHANGED',
  'filters/suburban_utilities/SUBURBAN_UTILITIES_CHANGED',
  'filters/suburban_wc/SUBURBAN_WC_TYPE_CHANGED',
  'filters/tags/ALL_TAGS_REMOVED',
  'filters/tags/TAG_REMOVED',
  'filters/tenants/TENANTS_CHANGED',
  'filters/total_floors/MAX_TOTAL_FLOORS_CHANGED',
  'filters/total_floors/MIN_TOTAL_FLOORS_CHANGED',
  'filters/underground_travel/UNDERGROUND_TRAVEL_CHANGED',
  'filters/wc/WC_COUNT_CHANGED',
  'filters/wc/WC_TYPE_CHANGED',
  'filters/windows/WINDOWS_TYPE_CHANGED',
  'filters/with_layout/WITH_LAYOUT_CHANGED',
  'filters/with_neighbors/WITH_NEIGHBORS_CHANGED',
  'filters/with_photo/WITH_PHOTO_CHANGED',
  'filters/words_exclude/WORD_EXCLUDE_ADDED',
  'filters/words_exclude/WORD_EXCLUDE_REMOVED',
  'filters/words_include/WORD_INCLUDE_ADDED',
  'filters/words_include/WORD_INCLUDE_REMOVED',
  'filters/coworking_access/COWORKING_ACCESS_CHANGED',
  'filters/workplace_count/MIN_WORKPLACE_COUNT_CHANGED',
  'filters/workplace_count/MAX_WORKPLACE_COUNT_CHANGED',
  'filters/coworking_offer_type/COWORKING_OFFER_TYPE_SELECTED',
  'filters/coworking_id/COWORKING_ID_SELECTED',
  ESuburbanOfferFilterActions.Changed,
  ESuburbanOfferFilterActions.Removed,
  'filters/json_query/change',
  IS_BY_COMMERCIAL_OWNER_ACTION_TYPE,
];

const actionsThatShouldStopUpdateMeta = [
  'filters/advanced/MODAL_CLOSED',
  'filters/location_switcher/LOCATION_SWITCHER_CLOSED',
  'filters/map/MAP_CLOSED',
  'filters/map/METRO_CLOSED',
];

export const metaRefreshRequest: IMetaRefreshRequestedAction = {
  type: 'filters/meta/META_REFRESH_REQUESTED',
};

export const metaRefreshFailed: IMetaRefreshFailedAction = {
  type: 'filters/meta/META_REFRESH_FAILED',
};

export const metaRefreshStopped: IMetaRefreshStoppedAction = {
  type: 'filters/meta/META_REFRESH_STOPPED',
};

export function* updateMetaSaga() {
  const { filters }: IAppState = yield select();

  /**
   * metaRefreshRequest должен отправляться до delay, иначе возникают баги с обновлением location при сортировке.
   * Если meta в данный момент уже обновлятся - экшен metaRefreshRequest избыточен.
   */
  if (!filters.jsonQueryCountRefresing) {
    yield put(metaRefreshRequest);
  }

  /**
   * delay нужен для уменьшения количества запросов при наборе текста в фильтрах.
   * См. задачу CD-66421
   */
  yield delay(300);
  try {
    const state: IAppState = yield select();
    const {
      filters: { jsonQuery },
      makeRequest,
    } = state;

    const nextJsonQuery = getSortReplaceCoworkingOfficeType(jsonQuery);

    const meta: IRequestMetaResponse = yield call(requestMeta, makeRequest, nextJsonQuery);
    const metaRefreshComplete: IMetaRefreshCompleteAction = {
      type: 'filters/meta/META_REFRESH_COMPLETE',
      meta,
    };

    saveJsonQueryInLocalStorage(nextJsonQuery);

    yield put(metaRefreshComplete);
  } catch (e) {
    yield put(metaRefreshFailed);
  }
}

/* istanbul ignore next */
export function* metaSaga() {
  let lastTask;
  while (true) {
    const action = yield take(actionsThatCouldChangeMeta);
    if (lastTask) {
      yield cancel(lastTask);
    }

    if (!actionsThatShouldStopUpdateMeta.includes(action.type)) {
      lastTask = yield fork(updateMetaSaga);
    } else {
      yield put(metaRefreshStopped);
    }
  }
}

/* istanbul ignore next */
export function metaReducer(state: IAppState, action: TActions): IAppState {
  switch (action.type) {
    case 'filters/meta/META_REFRESH_REQUESTED':
      return {
        ...state,
        filters: {
          ...state.filters,
          jsonQueryCountRefresing: true,
        },
      };
    case 'filters/meta/META_REFRESH_COMPLETE':
      return {
        ...state,
        filters: {
          ...state.filters,
          jsonQueryCount: action.meta.count,
          jsonQueryCountRefresing: false,
          jsonQueryFullUrl: action.meta.fullUrl,
          jsonQueryUrl: action.meta.url,
          queryString: action.meta.fullUrlWithoutSeo.substring(action.meta.fullUrlWithoutSeo.indexOf('?' + 1)),
          metaHeader: action.meta.h1,
        },
        /*
          При изменении фильтров меняются метаданные, то есть следующий запрос будет новым поиском
          Это значит, что с точки зрения охвата объявления начался новый поиск и нужно сбросить id старого

          Делаем это заранее в метаданных, чтобы во флоу событий поиска не оказалось двух разных guid
        */
        searchUuid: null,
      };
    case 'filters/meta/META_REFRESH_FAILED':
    case 'filters/meta/META_REFRESH_STOPPED':
      return {
        ...state,
        filters: {
          ...state.filters,
          jsonQueryCount: undefined,
          jsonQueryCountRefresing: false,
        },
      };
    case 'filters/search/SEARCH_REQUEST_FINISHED':
      return {
        ...state,
        filters: {
          ...state.filters,
          jsonQueryCount: undefined,
          jsonQueryCountRefresing: false,
        },
      };
    default:
      return state;
  }
}
